import ExtendedPath from "csi/core/ExtendedPath";
import Spec from "csi/core/Spec";
import { getSpecsForKeys } from "csi/middleware/attributeRule/RuleService";
import RuleStore from "csi/middleware/attributeRule/RuleStore";
import { StoreValues } from "csi/store/Values";
import { C8NodeType } from "csi/typings";
import { QueryLevel } from "csi/view/_query/Level";
import CustomViewGroupingStore from "csi/view/customview/CustomViewGroupingStore";
import CustomViewReportStore from "csi/view/customview/CustomViewReportStore";
import CustomViewSortingStore from "csi/view/customview/CustomViewSortingStore";
import { ILocaleProvider } from "designSystem/locale";
import { keyBy, map } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { ICustomViewFilter } from "src/c8-platform/type/customView";
import CustomViewAttributesStore from "../attributes/CustomViewAttributesStore";
import { GetValuesStoreFunction, checkIfCustomViewContainsComparisonOperator } from "../customViewDataManagementService";
import { ICustomViewUtil, populateAttributeKeysFromTargetClassDomain } from "../customViewService";
import { ICustomViewQueryOptions } from "../options/CustomViewOptions";

type IDataManagementCustomViewOptions = Pick<ICustomViewQueryOptions, "filterSettings" | "groupSettings" | "hideNestedReports" | "reportSettings" | "sortSettings">;

// TODO: Jake - move queryLevels out of options since they are required

export interface DataManagementOptions {
	/** custom spec for grouping */
	customGroupSpec?: Spec;

	/** indicates whether the view is gallery view or table view */
	isGalleryView?: boolean;

	/** levels for filtering in hierarchy and matrix views */
	queryLevels: QueryLevel[];

	/** determines if we show current user in filter pane */
	restrictFilterByCurrentUser?: boolean;
}

export default class CustomViewDataManagementStore implements ICustomViewUtil<ICustomViewQueryOptions>{
	@observable.ref attributeStore: CustomViewAttributesStore;
	@observable.ref private _ruleStores: RuleStore[];
	@observable.ref private _sortingStore: CustomViewSortingStore;
	@observable.ref private _groupingStore: CustomViewGroupingStore;
	@observable.ref private _reportStore: CustomViewReportStore;

	/** options for data management store */
	readonly dataManagementOptions: DataManagementOptions;

	/** all the available options for custom view */
	options: ICustomViewQueryOptions;

	loc: ILocaleProvider;

	readonly relativeType: C8NodeType;

	/** rule stores that can be configured based on visible attributes in the view */
	@computed get availableRuleStores(): RuleStore[] | undefined {
		const {
			attributeStore: { matrixFilterKeys },
			dataManagementOptions: { queryLevels },
			_ruleStores,
		} = this;

		return _ruleStores?.filter((ruleStore, i) => {
			const ql = queryLevels[i];
			return !ql.bForMatrix || matrixFilterKeys.includes(ql.id);
		});
	}

	/** get whether there is an invalid rule */
	@computed get hasInvalidRule(): boolean {
		return this._ruleStores?.some((ruleStore) => ruleStore?.hasInvalidRule);
	}

	/** values of custom view options from data management tab */
	@computed get optionValues(): IDataManagementCustomViewOptions {
		const { availableRuleStores, groupingStore, reportStore, sortingStore } = this;
		const groupSettings = groupingStore?.currentGroupSettings;
		const hideNestedReports = reportStore?.hideNestedReports;
		const reportSettings = reportStore?.currentReportSettings;
		const sortSettings = sortingStore?.currentSortSettings;
		let filterSettings: Record<string, ICustomViewFilter>;
		
		if (availableRuleStores) {
			filterSettings = {};
			availableRuleStores.forEach((ruleStore) => {
				const { currentFilterSettings, queryLevelId } = ruleStore;
				if (currentFilterSettings) {
					filterSettings[queryLevelId] = currentFilterSettings;
				}
			});
		}

		return {
			filterSettings,
			groupSettings,
			hideNestedReports,
			reportSettings,
			sortSettings,
		};
	};

	get sortingStore(): CustomViewSortingStore {
		return this._sortingStore;
	}

	get groupingStore(): CustomViewGroupingStore {
		return this._groupingStore;
	}

	get reportStore(): CustomViewReportStore {
		return this._reportStore;
	}

	@action _initDataManagement = (relativeType: C8NodeType): void => {
		const { attributeStore, dataManagementOptions, loc, options } = this;
		const { filterSettings, groupSettings, hideNestedReports, reportSettings, sortSettings } = options;
		const { attributeSpecsInView, mainLevel, selectedLevel } = attributeStore;
		const { customGroupSpec, isGalleryView, queryLevels, restrictFilterByCurrentUser } = dataManagementOptions;

		if (filterSettings) {
			// retrieve keys and specs for each query levels for filter settings
			const filterSettingSpecs = queryLevels.map((level) => {
				const extendedPath = new ExtendedPath(level.type);
				const keys = populateAttributeKeysFromTargetClassDomain(level.type);
				const revisionKeys = populateAttributeKeysFromTargetClassDomain(extendedPath.endClass.getRevisionType());
				const specs = getSpecsForKeys(keys.concat(revisionKeys)).concat(attributeSpecsInView);
				// eliminate duplicates created from combining specs from the path and specs in view
				const record: Record<string, Spec> = keyBy(specs, (spec) => spec.uniqueId);
				return map(record, (item) => item);
			});

			this._ruleStores = queryLevels.map((queryLevel, index) => {
				const onValuesStore: GetValuesStoreFunction = (spec: Spec): StoreValues => {
					return queryLevel.configurableStore.getValuesStore(spec, { bEmptyItem: false });
				};

				const filterSetting = filterSettings[queryLevel.id];
				return new RuleStore(
					loc,
					filterSettingSpecs[index],
					10,
					onValuesStore,
					filterSetting,
					false,
					attributeStore,
					checkIfCustomViewContainsComparisonOperator(filterSetting, selectedLevel.specBuilder),
					queryLevel.id,
					queryLevels.length > 1 ? queryLevel.name : null,
					!restrictFilterByCurrentUser,
					relativeType,
				);
			});
		}

		if (sortSettings) {
			// merge attributes selected in sort settings and attributes in view
			const sortSettingSpecs = [];
			sortSettings.forEach((sortLevel) => {
				if (!attributeSpecsInView.find((spec) => spec.uniqueId === sortLevel.key)) {
					const sortSettingSpec = mainLevel.specBuilder.getSpecForKey(sortLevel.key);
					sortSettingSpecs.push(sortSettingSpec);
				}
			});
			this._sortingStore = new CustomViewSortingStore(sortSettings, attributeStore, sortSettingSpecs);
		}

		if (groupSettings) {
			this._groupingStore = new CustomViewGroupingStore(groupSettings, attributeStore, customGroupSpec, isGalleryView);
		}

		if (reportSettings) {
			this._reportStore = new CustomViewReportStore(loc, reportSettings, attributeStore, hideNestedReports);
		}
	};

	/** @override */
	@action onChangeOptions = (options: ICustomViewQueryOptions): void => {
		this.options = options;
		this._initDataManagement(this.relativeType);
	};

	constructor(
		loc: ILocaleProvider,
		attributeStore: CustomViewAttributesStore,
		relativeType?: C8NodeType,
		options?: ICustomViewQueryOptions,
		dataManagementOptions?: DataManagementOptions,
	) {
		makeObservable(this);
		this.loc = loc;
		this.options = options;
		this.attributeStore = attributeStore;
		this.dataManagementOptions = dataManagementOptions;
		this.relativeType = relativeType;
		this._initDataManagement(relativeType);
	}
}